// kfe-lesson-report.js — the per-lesson Lesson Report screen (Brief 097).
//
// PORT TARGET: Classes/KFEModules/Reports/LessonReport/KFELessonReportVC
//              (iOS). Screen reference: Image 3 in the 2026-05-06 overseer
//              chat. Design: V1-UI-DESIGN-NOTES-V2-2026-05-06.md §7.3.
//
// Two sections, mirroring iOS canon (CLAUDE.md Rule #9 — do not redesign):
//   1. Report on Activity     — total-time stat, minutes-per-category bar
//                               chart, three word-count rows.
//   2. Report on Performance  — HAAI Recognition Index stat, per-test-instance
//                               line chart.
//
// DATA SOURCE & PHASING. The screen reads from `services.learner` data-layer
// APIs introduced by Brief 096 (per-lesson time / word-encounters) and Brief
// 095 (HAAI history). Those briefs are queued but not yet landed, so every
// call is guarded with a `typeof` check (`_call`): when an API is absent the
// datum reads as `null` and the corresponding element gracefully hides
// (success-criterion #13). The screen therefore renders correctly TODAY (in
// its fully-degraded "no data yet" state) and lights up automatically as
// 095/096 land — no consumer change. Tests/dev can also inject a synthetic
// report object via the `report` property to exercise the populated layout.
//
// GRACEFUL DEGRADATION (success-criterion #13): each element is individually
// conditional; if every element in a section is hidden, the section header
// hides too. If both sections are empty, a subtle empty-state line shows
// rather than a blank screen.
//
// Expected `services.learner` surface (Brief 096 §contract):
//   getLessonTotalTimeMs(lessonId): number | null
//   getLessonTimeByCategoryMs(lessonId): Record<CategoryKey, number> | null
//   getLessonUniqueWordCount(lessonId): number | null
//   getLessonNewWordCount(lessonId): number | null
//   getLessonHaaiHistory(lessonId): Array<{instance:number, value:number}> | null
// where CategoryKey ∈ CATEGORY_DEFS keys below. The single headline
// Recognition Index reads from an optional getLessonRecognitionIndex(lessonId)
// and otherwise falls back to the latest history entry.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t, getLanguage } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { loadPathway } from "../data/pathway-loader.js";
import { isLessonLocked } from "../data/pathway-builder.js";
import { isDevMode } from "../core/dev-mode.js";
import { navigate } from "../router.js";

const DEFAULT_PATHWAY_ID = "kfe_zero_to_fluency";

/**
 * The eleven iOS-canon activity categories (letter codes B–L), in canonical
 * letter order, with the single-letter code the data contract keys on
 * (`getLessonTimeByCategoryMs` returns a sparse `Record<"B".."L", number>`,
 * Brief 096 §contract), an i18n label key, and a distinct colour. Letter A is
 * reserved/unused in iOS canon — there is deliberately no `A` entry (Brief 096
 * §Source-verification). Labels are verbatim from iOS
 * `zh-Hans.lproj/Language.strings` "Pie Chart CategoryLetter" block (Brief 098
 * §1). The five that shipped in Brief 097 (B/D/E/H/J) keep their activity-wheel
 * palette tokens (tokens.css); the six added by Brief 098 (C/F/G/I/K/L) use
 * distinct categorical hues so all eleven stay legible if a lesson populates
 * every bucket. Order + the full 11-category set per Brief 098 / iOS
 * `KFEReportDataManager.m` (`master_item_categories.category_letter`).
 * @type {{letter: string, labelKey: string, color: string}[]}
 */
const CATEGORY_DEFS = [
  { letter: "B", labelKey: "lessonReport.catLanguageToBody", color: "var(--kfe-visualise)" },
  { letter: "C", labelKey: "lessonReport.catWriting",        color: "#9b59b6" },
  { letter: "D", labelKey: "lessonReport.catSelfManagement", color: "var(--kfe-read)" },
  { letter: "E", labelKey: "lessonReport.catStudyMethod",    color: "var(--kfe-speak)" },
  { letter: "F", labelKey: "lessonReport.catReview",         color: "#1abc9c" },
  { letter: "G", labelKey: "lessonReport.catReading",        color: "#34495e" },
  { letter: "H", labelKey: "lessonReport.catTesting",        color: "var(--kfe-test)" },
  { letter: "I", labelKey: "lessonReport.catSpeaking",       color: "#d35400" },
  { letter: "J", labelKey: "lessonReport.catListening",      color: "var(--kfe-listen)" },
  { letter: "K", labelKey: "lessonReport.catNewWord",        color: "#7f8c8d" },
  { letter: "L", labelKey: "lessonReport.catGrammarPoints",  color: "#f1c40f" },
];

/** @param {number} ms @returns {number} minutes, rounded to one decimal */
function msToMinutes(ms) {
  return Math.round((ms / 60000) * 10) / 10;
}

/**
 * @typedef {object} ReportData
 * @property {number|null} totalTimeMs
 * @property {Record<string, number>|null} byCategoryMs   keyed by letter code B–L (Brief 096/098)
 * @property {number|null} uniqueWords
 * @property {number|null} newWords
 * @property {number|null} recognitionIndex
 * @property {Array<{instance:number, value:number}>|null} haaiHistory
 */

export class KFELessonReport extends KFEComponent {
  // `report` is an optional injectable ReportData (tests / synthetic dev).
  static props = ["lessonId", "report"];
  static styles = `
    :host { display: block; }

    /* Phone-width centre column on desktop (V1-UI-DESIGN-NOTES §5): the
       report sits in the same ~480px column as the exercise screens. */
    .report { max-width: 480px; margin: 0 auto; }

    .toolbar { display: flex; justify-content: flex-end; margin-bottom: 0.25rem; }
    button.share {
      background: transparent; border: 0; color: var(--kfe-fg);
      font-size: 1.25rem; line-height: 1; padding: 0.25rem; cursor: pointer;
      border-radius: var(--kfe-radius);
    }
    button.share:hover { background: var(--kfe-surface-hover); }
    .share-msg { color: var(--kfe-muted); font-size: 0.8rem; text-align: right; margin: 0.25rem 0 0; }

    .section { margin: 0 0 2rem; }
    .section-title {
      font-size: var(--kfe-h2); color: var(--kfe-fg);
      margin: 0 0 0.75rem; padding-bottom: 0.25rem;
      border-bottom: 2px solid var(--kfe-accent);
    }

    .chart-caption { color: var(--kfe-muted); font-size: 0.8rem; text-align: center; margin: 0.25rem 0 0; }
    .unavailable { color: var(--kfe-muted); font-size: 0.85rem; text-align: center; padding: 1rem 0; }

    .empty { color: var(--kfe-muted); padding: 2rem; text-align: center; }

    /* Primary CTA — orange/yellow background, dark text (V1-UI-DESIGN-NOTES §8). */
    .continue {
      display: block; width: 100%;
      margin-top: 1rem; padding: 0.875rem 1rem;
      background: var(--kfe-accent); color: var(--kfe-bg);
      border: 0; border-radius: var(--kfe-radius);
      font-size: 1.05rem; font-weight: 700; cursor: pointer;
      text-align: center;
    }
    .continue:hover { filter: brightness(1.05); }
    /* Brief 135 NAV-1: greyed Continue when the next lesson is gated. */
    .continue[disabled] {
      background: var(--kfe-surface); color: var(--kfe-muted);
      cursor: not-allowed; filter: none;
    }
  `;

  /** @type {ReportData|null} */
  _data = null;
  _loaded = false;
  /** @type {string} */
  _shareMsg = "";

  /** Cached forward-nav target (Brief 135 NAV-1): resolved once in onConnected
   * so the Continue button can render its locked state and _onContinue needn't
   * re-resolve on click.
   * @type {{nextLessonId: string|null, stageId: string|null}} */
  _nav = { nextLessonId: null, stageId: null };
  /** True when the resolved next lesson is gated → Continue greys out instead of
   * navigating into a locked lesson (Brief 135 NAV-1). Only the next-lesson hop
   * locks; the stage/home fallbacks always stay live. */
  _nextLocked = false;

  async onConnected() {
    const onI18n = () => {
      this._dispatchHeaderTitle();
      this._scheduleRender();
    };
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    this._dispatchHeaderTitle();

    const lessonId = String(/** @type {any} */ (this).lessonId ?? "");

    // Injected synthetic data (tests / dev) short-circuits the service reads.
    const injected = /** @type {ReportData|undefined} */ (/** @type {any} */ (this).report);
    if (injected) {
      this._data = injected;
      this._loaded = true;
      this._scheduleRender();
      return;
    }

    this._data = await this._acquire(lessonId);
    await this._resolveNavAndLock(lessonId);
    this._loaded = true;
    this._scheduleRender();
  }

  /** Resolve the forward-nav target + its lock state for the Continue button
   * (Brief 135 NAV-1). Dev mode keeps Continue live regardless of gating.
   * @param {string} lessonId */
  async _resolveNavAndLock(lessonId) {
    this._nav = await this._resolveNav(lessonId);
    if (this._nav.nextLessonId && !isDevMode()) {
      const pathwayId = /** @type {string} */ (
        (await services.learner.getSetting("active_pathway")) || DEFAULT_PATHWAY_ID
      );
      this._nextLocked = await isLessonLocked(pathwayId, this._nav.nextLessonId);
    } else {
      this._nextLocked = false;
    }
  }

  /** @param {string} lessonId @returns {Promise<ReportData>} */
  async _acquire(lessonId) {
    const [totalTimeMs, byCategoryMs, uniqueWords, newWords, haaiHistory, riExplicit] =
      await Promise.all([
        this._call("getLessonTotalTimeMs", lessonId),
        this._call("getLessonTimeByCategoryMs", lessonId),
        this._call("getLessonUniqueWordCount", lessonId),
        this._call("getLessonNewWordCount", lessonId),
        this._call("getLessonHaaiHistory", lessonId),
        this._call("getLessonRecognitionIndex", lessonId),
      ]);

    // Headline Recognition Index: prefer an explicit API, else the most recent
    // history entry (the current per-test-instance value).
    let recognitionIndex = /** @type {number|null} */ (riExplicit);
    if (recognitionIndex == null && Array.isArray(haaiHistory) && haaiHistory.length) {
      recognitionIndex = haaiHistory[haaiHistory.length - 1]?.value ?? null;
    }

    return {
      totalTimeMs: /** @type {number|null} */ (totalTimeMs),
      byCategoryMs: /** @type {Record<string,number>|null} */ (byCategoryMs),
      uniqueWords: /** @type {number|null} */ (uniqueWords),
      newWords: /** @type {number|null} */ (newWords),
      recognitionIndex,
      haaiHistory: /** @type {Array<{instance:number,value:number}>|null} */ (haaiHistory),
    };
  }

  /** Guarded data-layer call: returns null if the API is absent or throws.
   * @param {string} name @param {string} lessonId @returns {Promise<unknown>} */
  async _call(name, lessonId) {
    const fn = /** @type {any} */ (services.learner)[name];
    if (typeof fn !== "function") return null;
    try {
      const v = await fn.call(services.learner, lessonId);
      return v === undefined ? null : v;
    } catch (e) {
      console.warn(`[lesson-report] ${name} failed`, e);
      return null;
    }
  }

  _dispatchHeaderTitle() {
    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title: t("lessonReport.title") },
      bubbles: true,
      composed: true,
    }));
  }

  _onShare = () => {
    // Share mechanism is deferred to a separate future brief (May overseer
    // doc §10). Brief 097 renders the icon but only shows a placeholder.
    // FUTURE: wire Web Share API / iOS "Photo + caption" target here.
    this._shareMsg = t("lessonReport.shareUnavailable");
    this._scheduleRender();
  };

  _onContinue = () => {
    // Brief 135 NAV-1: a locked next lesson is non-navigable (the button is also
    // rendered disabled). Fall through to the stage / home destinations, which
    // are never gated.
    const { nextLessonId, stageId } = this._nav;
    if (nextLessonId && !this._nextLocked) navigate(`/lesson/${nextLessonId}`);
    else if (stageId) navigate(`/stage/${stageId}`);
    else navigate("/");
  };

  /** Resolve the next lesson + containing stage from the active pathway tree.
   * @param {string} lessonId
   * @returns {Promise<{nextLessonId: string|null, stageId: string|null}>} */
  async _resolveNav(lessonId) {
    try {
      const pathwayId = /** @type {string} */ (
        (await services.learner.getSetting("active_pathway")) || DEFAULT_PATHWAY_ID
      );
      const tree = await loadPathway(pathwayId);
      /** @type {{id: string, stageId: string}[]} */
      const flat = [];
      for (const stage of tree.stages || []) {
        for (const lesson of stage.lessons || []) {
          // Gates are not navigable lessons.
          if (lesson && lesson.type !== "gate") {
            flat.push({ id: String(lesson.id), stageId: String(stage.id) });
          }
        }
      }
      const want = String(lessonId);
      const idx = flat.findIndex((l) => l.id === want);
      if (idx === -1) return { nextLessonId: null, stageId: null };
      const next = flat[idx + 1];
      return { nextLessonId: next ? next.id : null, stageId: flat[idx].stageId };
    } catch (e) {
      console.warn("[lesson-report] next-lesson resolve failed", e);
      return { nextLessonId: null, stageId: null };
    }
  }

  // ---- Section builders -------------------------------------------------

  /** @param {ReportData} d @returns {Node|null} the Activity section, or null if empty */
  _renderActivity(d) {
    const hasTotal = d.totalTimeMs != null;
    const hasCategory = d.byCategoryMs != null;
    const hasUnique = d.uniqueWords != null;
    const hasNew = d.newWords != null;
    // "% new" needs both counts and a non-zero denominator (#13).
    const hasPctNew = hasUnique && hasNew && /** @type {number} */ (d.uniqueWords) > 0;

    // Section visible iff at least one real datum exists.
    if (!hasTotal && !hasCategory && !hasUnique && !hasNew) return null;

    const children = [];

    if (hasTotal) {
      const card = document.createElement("kfe-stat-card");
      /** @type {any} */ (card).value = msToMinutes(/** @type {number} */ (d.totalTimeMs));
      /** @type {any} */ (card).unit = t("lessonReport.minUnit");
      /** @type {any} */ (card).label = t("lessonReport.totalTime");
      children.push(card);
    }

    // Walk the canonical B→L category order, keep only the buckets the lesson
    // actually populated (non-zero ms). Empty buckets don't render; if every
    // bucket is empty (or the map is null), the chart hides and the section
    // shows the "unavailable" note (#13 / Brief 098 §2).
    const byLetter = /** @type {Record<string,number>|null} */ (d.byCategoryMs);
    const active = byLetter
      ? CATEGORY_DEFS.filter((c) => (byLetter[c.letter] ?? 0) > 0)
      : [];

    // Label format (Brief 098 §4/§5): each bar reads "<letter> <name>", e.g.
    // "H 测试闯关" / "H Testing". The letter prefix keeps the mapping legible
    // against the iOS CMS item-edit dropdown. A real lesson populates ~3–6
    // categories, which read cleanly at the report's phone-width column (capped
    // at 480px). The synthetic all-11 worst case crowds the axis at 375px even
    // in Chinese (single-line CJK labels are ~5 glyphs and CJK doesn't wrap),
    // so above DENSE_LABEL_LIMIT bars we fall back to letter-code-only labels —
    // the documented §4 fallback. The letter alone stays the CMS-cross-
    // referenceable anchor; the minutes sit above each bar.
    const DENSE_LABEL_LIMIT = 6;
    const lettersOnly = active.length > DENSE_LABEL_LIMIT;
    const bars = active.map((c) => ({
      label: lettersOnly ? c.letter : `${c.letter} ${t(c.labelKey)}`,
      value: Math.round((byLetter ? byLetter[c.letter] ?? 0 : 0) / 60000),
      color: c.color,
    }));

    if (bars.length > 0) {
      const chart = document.createElement("kfe-bar-chart");
      /** @type {any} */ (chart).bars = bars;
      /** @type {any} */ (chart).yLabel = t("lessonReport.timeAxis");
      children.push(chart);
    } else {
      // Bar chart hidden but section otherwise present: subtle note (#13).
      children.push(html`<p class="unavailable">${t("lessonReport.timeBreakdownUnavailable")}</p>`);
    }

    if (hasUnique) {
      children.push(this._statRow(t("lessonReport.uniqueWords"), String(d.uniqueWords)));
    }
    if (hasNew) {
      children.push(this._statRow(t("lessonReport.newWords"), String(d.newWords)));
    }
    if (hasPctNew) {
      const pct = Math.round(
        (/** @type {number} */ (d.newWords) / /** @type {number} */ (d.uniqueWords)) * 100,
      );
      children.push(this._statRow(t("lessonReport.percentNew"), `${pct}%`));
    }

    return html`
      <section class="section">
        <h2 class="section-title">${t("lessonReport.activitySection")}</h2>
        ${children}
      </section>
    `;
  }

  /** @param {ReportData} d @returns {Node|null} the Performance section, or null if empty */
  _renderPerformance(d) {
    const hasRI = d.recognitionIndex != null;
    const history = Array.isArray(d.haaiHistory) ? d.haaiHistory : [];
    const hasGraph = history.length > 0;

    if (!hasRI && !hasGraph) return null;

    const children = [];

    if (hasRI) {
      const card = document.createElement("kfe-stat-card");
      /** @type {any} */ (card).value = Math.round(/** @type {number} */ (d.recognitionIndex));
      /** @type {any} */ (card).unit = "%";
      /** @type {any} */ (card).label = t("lessonReport.recognitionIndex");
      children.push(card);
    }

    if (hasGraph) {
      const chart = document.createElement("kfe-line-chart");
      /** @type {any} */ (chart).points = history.map((h) => ({ x: h.instance, y: h.value }));
      /** @type {any} */ (chart).yRange = [0, 100];
      children.push(chart);
      children.push(html`<p class="chart-caption">${t("lessonReport.recognitionGraph")}</p>`);
    }

    return html`
      <section class="section">
        <h2 class="section-title">${t("lessonReport.performanceSection")}</h2>
        ${children}
      </section>
    `;
  }

  /** @param {string} label @param {string} value */
  _statRow(label, value) {
    const row = document.createElement("kfe-stat-row");
    /** @type {any} */ (row).label = label;
    /** @type {any} */ (row).value = value;
    return row;
  }

  render() {
    if (!this._loaded) {
      return html`<p class="empty">${t("loading")}</p>`;
    }
    const d = this._data ?? {
      totalTimeMs: null, byCategoryMs: null, uniqueWords: null,
      newWords: null, recognitionIndex: null, haaiHistory: null,
    };

    const activity = this._renderActivity(d);
    const performance = this._renderPerformance(d);
    const anyContent = activity || performance;

    return html`
      <div class="report">
        <div class="toolbar">
          <button class="share" type="button" aria-label="${t("lessonReport.share")}"
                  on:click=${this._onShare}>⤴</button>
        </div>
        ${this._shareMsg ? html`<p class="share-msg">${this._shareMsg}</p>` : ""}
        ${anyContent ? "" : html`<p class="empty">${t("lessonReport.empty")}</p>`}
        ${activity || ""}
        ${performance || ""}
        ${this._nextLocked
          ? html`<button class="continue" type="button" disabled
                  title="${t("lessonDetail.nextLessonLocked")}">${t("lessonReport.continue")}</button>`
          : html`<button class="continue" type="button" on:click=${this._onContinue}>${t("lessonReport.continue")}</button>`}
      </div>
    `;
  }
}
